import React, {
    FC,
    useState,
    useRef,
    useEffect,
    ReactNode,
    KeyboardEventHandler,
    MouseEventHandler,
    useCallback,
} from 'react';
import classnames from 'classnames';
import ArrowDownIcon from 'dibs-icons/exports/ArrowDown';
import DropdownItem from './DropdownItem';
import styles from './styles/Dropdown.scss';
import { useUID } from 'dibs-uid';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

const ESC = 27;
const ENTER = 13;
const SPACE = 32;
const UP = 38;
const DOWN = 40;

function createOptionId(base: string, index: number): string {
    return `${base}-option-${index}`;
}

function getKeyValue(
    option: string | { key: string; value: string }
): { key: string; value: string } {
    return typeof option === 'object' ? option : { key: option, value: option };
}

type Props = {
    dataTn: string;
    options: ({ key: string; value: string } | string)[];
    selectedOption: string;
    onChange: (selected: string) => void;
    label: string;
    renderOptionDisplay?: (option: string) => ReactNode;
    dropdownClass?: string;
};

const Dropdown: FC<Props> = ({
    dataTn,
    label,
    options,
    selectedOption,
    onChange,
    dropdownClass,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const selectedIndex = options.findIndex(item => {
        const { key } = getKeyValue(item);
        return selectedOption === key;
    });
    const [activeDescendentIndex, setActiveDescendentIndex] = useState<number>(selectedIndex);
    useEffect(() => setActiveDescendentIndex(selectedIndex), [selectedIndex]);

    const labelUid = useUID();
    const listUid = useUID();
    const buttonUid = useUID();

    const listBoxRef = useRef<HTMLUListElement>(null);

    const labelledBy = [labelUid, buttonUid].filter(filterNulls).join(' ');

    const closeDropdown = useCallback(() => {
        setIsExpanded(false);
        setActiveDescendentIndex(selectedIndex);
    }, [selectedIndex]);

    useEffect(() => {
        if (isExpanded) {
            window.addEventListener('click', closeDropdown);
        }
        return () => window.removeEventListener('click', closeDropdown);
    }, [isExpanded, closeDropdown]);

    const handleButtonClick: MouseEventHandler = event => {
        event.preventDefault();
        setIsExpanded(true);
        setTimeout(() => {
            if (listBoxRef.current) listBoxRef.current.focus();
        });
    };

    const createHandleOptionClick = (value: string): MouseEventHandler => {
        const handleOptionClick: MouseEventHandler = event => {
            event.preventDefault();
            event.stopPropagation();
            onChange(value);
            closeDropdown();
        };
        return handleOptionClick;
    };

    const handleListBoxKeyDown: KeyboardEventHandler = event => {
        const keyCode = event.which || event.keyCode;
        // only dropdown controls should preventDefault. since this fires for all key presses,
        // call preventDefault only on matched controls
        switch (keyCode) {
            case UP:
                event.preventDefault();
                setActiveDescendentIndex(prev => Math.max(prev - 1, 0));
                break;
            case DOWN:
                event.preventDefault();
                setActiveDescendentIndex(prev => Math.min(prev + 1, options.length - 1));
                break;
            case ENTER:
            case SPACE: {
                event.preventDefault();
                event.stopPropagation();
                const active = options[activeDescendentIndex];
                const { value } = getKeyValue(active);
                onChange(value);
                closeDropdown();
                break;
            }
            case ESC:
                event.preventDefault();
                closeDropdown();
                break;
            default:
                // do not preventDefault to preserve other keyboard functionality, such as tab
                break;
        }
    };

    return (
        <div className={styles.container}>
            <div id={labelUid || undefined} className={styles.label}>
                {label}
            </div>
            <div className={styles.buttonListContainer}>
                <button
                    aria-labelledby={labelledBy}
                    aria-haspopup="listbox"
                    aria-expanded={isExpanded ? 'true' : 'false'}
                    aria-controls={listUid || undefined}
                    data-tn={dataTn}
                    id={buttonUid || undefined}
                    className={styles.button}
                    onClick={handleButtonClick}
                >
                    {selectedOption}
                    <ArrowDownIcon className={styles.arrow} />
                </button>
                <ul
                    id={listUid || undefined}
                    ref={listBoxRef}
                    className={classnames(styles.dropdown, dropdownClass, {
                        [styles.hidden]: !isExpanded,
                    })}
                    onKeyDown={handleListBoxKeyDown}
                    onBlur={closeDropdown}
                    tabIndex={-1}
                    role="listbox"
                    aria-labelledby={labelUid || undefined}
                    aria-activedescendant={createOptionId(
                        labelUid || '',
                        activeDescendentIndex || 0
                    )}
                >
                    {options.map((optionItem, i) => {
                        const id = `${labelUid}-option-${i}`;
                        const { key, value } = getKeyValue(optionItem);
                        return (
                            <DropdownItem
                                key={key}
                                onClick={createHandleOptionClick(value)}
                                dataTn={`${dataTn}-item-${key}`}
                                label={key}
                                selected={selectedOption === key}
                                id={id}
                                focused={i === activeDescendentIndex}
                            />
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};

export default Dropdown;
